import { Input } from "@/components/ui/input";
import permissions from "@/permissions";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import Icon from "@/icons";
import Tippy from "@tippyjs/react";
import { ManageRole } from "@/components/ContextMenu/Role";
import BotTag from "@/components/Tags/Bot";
import ErrorBoundary from "@/ErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

export const SettingsTeamRoles = ({ me, team, user }: { me: any, team: any, user: any }) => {
    const { teamId } = useParams<{ teamId: string }>();

    const req = async () => {
        const roles = await fetch(`__SITE_BASE__/api/teams/${teamId}/roles`);
        return (await roles.json()).roles as any[];
    };


    const queryClient = useQueryClient();
    const members = (queryClient.getQueryData(["team", teamId, "members"]) as any)?.members as any;
    const [roles, setRoles] = useState<any[]>([]);
    const [selectedRole, setSelectedRole] = useState<any>({});

    useEffect(() => {
        req().then((roles) => {
            setRoles(roles.sort(
                (a, b) => b.priority - a.priority
            )); setSelectedRole(roles.length > 0 ? roles[0] : {});
        });
    }, []);
    const [isChanged, setIsChanged] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const permissionsDetails = [
        {
            name: "Administrator",
            onMap: "ADMINISTRATOR",
            description: "Allows you to update the team settings.",
            map: permissions.get("ADMINISTRATOR"),
        },
        {
            name: "Manage Channels",
            onMap: "MANAGE_CHANNELS",
            description: "Allows you to manage channels.",
            map: permissions.get("MANAGE_CHANNELS"),
        },
        {
            name: "Manage Roles",
            onMap: "MANAGE_ROLES",
            description: "Allows you to manage roles.",
            map: permissions.get("MANAGE_ROLES"),
        },
        {
            name: "Manage Team",
            onMap: "MANAGE_TEAM",
            description: "Allows you to manage the team.",
            map: permissions.get("MANAGE_TEAM"),
        },
        {
            name: "View Audit Log",
            onMap: "VIEW_AUDIT_LOG",
            description: "Allows you to view the audit log.",
            map: permissions.get("VIEW_AUDIT_LOG"),
        },
        {
            name: "Ban Members",
            onMap: "BAN_MEMBERS",
            description: "Allows you to ban members.",
            map: permissions.get("BAN_MEMBERS"),
        },
        {
            name: "Delete Messages",
            onMap: "DELETE_MESSAGES",
            description: "Allows you to delete messages.",
            map: permissions.get("DELETE_MESSAGES"),
        },
        {
            name: "Manage Applications",
            onMap: "MANAGE_APPLICATIONS",
            description: "Allows you to manage applications.",
            map: permissions.get("MANAGE_APPLICATIONS"),
        },
        {
            name: "Join Tournaments",
            onMap: "JOIN_TOURNAMENTS",
            description: "Allows you to join tournaments.",
            map: permissions.get("JOIN_TOURNAMENTS"),
        },
        {
            name: "Create Invites",
            onMap: "CREATE_INVITES",
            description: "Allows you to create invites.",
            map: permissions.get("CREATE_INVITES"),
        }
    ]


    const handleRoleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedRoles = roles.map(role =>
            role.id === selectedRole.id ? { ...role, name: e.target.value } : role
        );
        setRoles(updatedRoles);
        setSelectedRole({ ...selectedRole, name: e.target.value });
        setIsChanged(true);
    };

    const handleDragEnd = async (result: any) => {
        if (!result.destination) return;
        if (result.destination.index === result.source.index) return;
        const reorderedRoles = Array.from(roles);
        const [movedRole] = reorderedRoles.splice(result.source.index, 1);
        reorderedRoles.splice(result.destination.index, 0, movedRole);
        const updatedRoles = reorderedRoles.map((role, index) => ({
            ...role,
            priority: reorderedRoles.length - index - 1,
        }));

        const r = await fetch(`__SITE_BASE__/api/teams/${teamId}/roles-priority`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedRoles.map(role => role.id)),
        })
        const json = await r.json();
        if (json.message) return;
        setRoles(json.roles);
    };

    const addRole = async () => {
        const create = await fetch(`__SITE_BASE__/api/teams/${teamId}/roles`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
        });
        const newRole = await create.json();
        if (newRole.message) return setSaveError(newRole.message);
        setRoles([...roles, newRole.role]);
        setSelectedRole(newRole.role);
    }


    const setRoleColor = (t: null | string) => {
        const color = document.querySelector("input[type='color']") as HTMLInputElement;
        const updatedRoles = roles.map(role =>
            role.id === selectedRole.id ? { ...role, color: t ?? color.value } : role
        );
        setRoles(updatedRoles);
        setSelectedRole({ ...selectedRole, color: color.value });
        setIsChanged(true);
    }

    const saveChanges = async () => {
        const req = await fetch(`__SITE_BASE__/api/teams/${teamId}/roles`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(roles),
        });
        const json = await req.json();
        if (json.message) return setSaveError(json.message);
        setIsChanged(false);
    }
    const handleDeleteRole = async (roleId: string) => {
        const req = await fetch(`__SITE_BASE__/api/teams/${teamId}/roles/${roleId}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
        });
        if (req.status !== 200) return;
        setRoles(roles.filter(role => role.id !== roleId));
        if (selectedRole.id === roleId) setSelectedRole(roles.length > 0 ? roles[0] : {});
    }

    let userHighRole = roles.reduce((acc) => {
        if (team.createdBy === user.id) return 999;
        let userM = members?.find((member: any) => member.id === user.id);
        if (!userM.roles.includes(team.id)) userM?.roles.push(team.id);
        userM = userM?.roles.map((role: any) => roles.find((r: any) => r.id === role)?.priority);
        if (!userM) return acc;
        return Math.min(acc, Math.max(...userM));
    }, 0);

    const cloneRole = async (roleId: string) => {
        const create = await fetch(`__SITE_BASE__/api/teams/${teamId}/roles/${roleId}/clone`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        });
        const newRole = await create.json();
        if (newRole.message) return setSaveError(newRole.message);
        setRoles([...roles, newRole.role]);
        setSelectedRole(newRole.role);
        toast.success("Role cloned successfully.");
    }

    const defaultColors = [
        {
            name: "Blue",
            color: "#7289da",
        },
        {
            name: "Red",
            color: "#f04747",
        },
        {
            name: "Green",
            color: "#43b581",
        },
        {
            name: "Yellow",
            color: "#f0b528",
        },
        {
            name: "Purple",
            color: "#9b51e0",
        },
        {
            name: "Gray",
            color: "#747f8d",
        },
        {
            name: "Orange",
            color: "#f26522",
        },
        {
            name: "Teal",
            color: "#4ade80",
        },
        {
            name: "Magenta",
            color: "#eb459e",
        },
        {
            name: "Pink",
            color: "#f47fff",
        },
        {
            name: "Brown",
            color: "#a3754b",
        },
        {
            name: "Gold",
            color: "#f0b528",
        },
        {
            name: "Navy",
            color: "#344bd8",
        },
        {
            name: "Lime",
            color: "#a5c749",
        },
        {
            name: "Maroon",
            color: "#8e3f3f",
        },
        {
            name: "Olive",
            color: "#677f67",
        },

    ]

    const defaultGradientColors = [
        {
            name: "Candy",
            color1: "#ff7eb3",
            color2: "#ff758c",
        },
        {
            name: "Rainbow",
            color1: "#ff0000",
            color2: "#ff00ff",
        },
        {
            name: "Ocean",
            color1: "#1c92d2",
            color2: "#f2fcfe",
        },
        {
            name: "Forest",
            color1: "#2ebf91",
            color2: "#8360c3",
        },
        {
            name: "Sunset",
            color1: "#ff6a00",
            color2: "#ee0979",
        },
        {
            name: "Lavender",
            color1: "#e0c3fc",
            color2: "#8ec5fc",
        },
        {
            name: "Peach",
            color1: "#ff9a9e",
            color2: "#fad0c4",
        },
        {
            name: "Berry",
            color1: "#b993d6",
            color2: "#8ca6db",
        },
        {
            name: "Aurora",
            color1: "#00c6ff",
            color2: "#0072ff",
        },
        {
            name: "Flamingo",
            color1: "#fbc2eb",
            color2: "#a6c1ee",
        },
        {
            name: "Galaxy",
            color1: "#5f72be",
            color2: "#9b23ea",
        },
        {
            name: "Sunrise",
            color1: "#ff9a9e",
            color2: "#fecfef",
        },
        {
            name: "Watermelon",
            color1: "#fc6076",
            color2: "#ff9a44",
        },
        {
            name: "Dream",
            color1: "#a18cd1",
            color2: "#fbc2eb",
        },
        {
            name: "Mint",
            color1: "#00bf8f",
            color2: "#001510",
        },
        {
            name: "Skyline",
            color1: "#0083b0",
            color2: "#00b4db",
        }
    ];



    useEffect(() => {
        if (!saveError) return;
        toast.error(saveError);
    }, [saveError]);

    return (
        <>

            <div className="sm:flex sm:flex-col w-full h-full mb-12">
                <div className="sm:flex sm:flex-col w-full">
                    <div className="sm:flex items-start min-w-full">
                        <div className="flex items-center">
                            <div className="flex flex-col items-start w-full">
                                <h1 className="text-2xl font-semibold text-white">Roles</h1>
                                <div className="flex flex-col w-full">
                                    <div className="mt-4 overflow-y-auto max-h-[16rem] sm:max-h-[45rem] w-full sm:min-w-[16rem]" style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(39 42 47)" }}>
                                        <ErrorBoundary>
                                            <DragDropContext onDragEnd={handleDragEnd}>
                                                <Droppable droppableId="roles">
                                                    {(provided) => (
                                                        <div {...provided.droppableProps} ref={provided.innerRef}>
                                                            {roles.sort((a, b) => b.priority - a.priority).map((role, index) => (
                                                                <ManageRole roleId={role.id} key={`${role.id}-manage`} handleDeleteRole={() => handleDeleteRole(role.id)} canBeDeleted={userHighRole > role.priority && role.botScope.userId === null && role.id !== team.id} handleCloneRole={() => cloneRole(role.id)}>
                                                                    <Draggable key={`${role.id}-drag`} draggableId={role.id} index={index} isDragDisabled={userHighRole <= role.priority}>
                                                                        {(provided) => (
                                                                            <div
                                                                                ref={provided.innerRef}
                                                                                {...provided.draggableProps}
                                                                                {...provided.dragHandleProps}
                                                                                className={`flex items-center justify-between w-full cursor-pointer hover:bg-brand-default rounded-lg px-4 py-2 mb-2 transition-all ${selectedRole.id === role.id ? 'bg-brand-dark' : 'bg-brand-darker'} ${userHighRole <= role.priority ? 'opacity-50' : ''}`}
                                                                                onClick={() => setSelectedRole(role)}
                                                                            >
                                                                                <div className="flex items-center">
                                                                                    {userHighRole <= role.priority && (
                                                                                        <Tippy content="You cannot edit this role." placement="top">
                                                                                            <div className="mr-2">
                                                                                                <Icon name="lock" size="16" color="gray" />
                                                                                            </div>
                                                                                        </Tippy>
                                                                                    )}
                                                                                    {role.id === team.id && userHighRole > role.priority && (
                                                                                        <Tippy content="This role is the default role for the team." placement="top">
                                                                                            <div className="mr-2 text-gray-400">
                                                                                                <Icon name="profile" size="16" />
                                                                                            </div>
                                                                                        </Tippy>
                                                                                    )}

                                                                                    <div className="w-4 h-4 rounded-full" style={{ background: role.color2 ? `linear-gradient(90deg, ${role.color}, ${role.color2})` : role.color }}></div>
                                                                                    <span className="ml-2 text-white">{role.name.length > 20 ? role.name.slice(0, 20) + "..." : role.name}</span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                </ManageRole>
                                                            ))}
                                                            {provided.placeholder}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </DragDropContext>
                                        </ErrorBoundary>
                                    </div>

                                    <div className="flex flex-col items-center  justify-between w-full cursor-pointer bg-brand-default hover:bg-brand-profile transition-all rounded-lg py-1 px-2 mb-2" onClick={addRole}>
                                        <div className="flex items-center">
                                            <Icon name="plus" />
                                            <span className="ml-2 text-white">Create New Role</span>
                                        </div>
                                    </div>

                                    {isChanged && (
                                        <div className="flex items-center justify-between w-full cursor-pointer bg-brand-green hover:bg-brand-green transition-all rounded-lg py-1 px-2 mb-2" onClick={saveChanges}>
                                            <div className="flex items-center">
                                                <Icon name="send" size="12" />
                                                <span className="ml-2 text-white">Save Changes</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {selectedRole.id && (
                            <div className="flex flex-col items-start sm:ml-8 sm:w-3/4 max-h-[55rem] overflow-y-auto sm:p-5" style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(39 42 47)" }}>
                                <ErrorBoundary>
                                    {userHighRole <= selectedRole.priority && (
                                        <div className="mb-4 w-full">
                                            <div className="flex items-center w-full bg-brand-default rounded-lg border py-5 border-brand-profile/40">
                                                <div className="w-16 text-center text-white items-center justify-center flex">
                                                    <Icon name="lock" size="24" />
                                                </div>
                                                <div className="flex flex-col items-start">
                                                    <span className="text-gray-300 text-sm  font-semibold">Role is locked because it is a higher rank than your highest role</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedRole.botScope.userId && userHighRole >= selectedRole.priority && (
                                        <div className="mb-4 w-full">
                                            <div className="flex items-center w-full bg-brand-darker rounded-lg border py-5 border-red-700/40 bg-red-300/50">
                                                <div className="w-16 text-center items-center justify-center flex">
                                                    <BotTag isTest={true} />
                                                </div>

                                                <div className="flex flex-col items-start">
                                                    <span className="text-white font-semibold">This role is a bot role and cannot be deleted.</span>
                                                    <p className="text-gray-300 text-sm">Kick the <strong>{members.find((member: any) => member.id === selectedRole.botScope.userId)?.username ?? "bot"}</strong> bot to delete this role.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedRole.id == team.id && userHighRole > selectedRole.priority && (
                                        <div className="mb-4 w-full">
                                            <div className="flex items-center w-full bg-brand-darker rounded-lg border py-5 border-green-700/40 bg-green-300/50">
                                                <div className="w-16 text-center items-center justify-center flex">
                                                    <Icon name="profile" size="24" />
                                                </div>

                                                <div className="flex flex-col items-start">
                                                    <span className="text-white font-semibold">This role is the default role for the team.</span>
                                                    <p className="text-gray-300 text-sm">You cannot delete this role.</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-center">
                                        <div className="flex flex-col items-start">
                                            <h1 className="text-2xl font-semibold text-white">Role Settings</h1>
                                            <h3 className="text-gray-300 text-sm mt-4">Role Name</h3>
                                            <div className="w-full">
                                                <div className="flex items-center justify-between w-full bg-brand-darker rounded-lg py-2 mb-2">
                                                    <Input
                                                        className="bg-brand-default rounded-t-lg w-80 sm:w-[30rem] border-r-0 border-l-0 border-t-0 border-b-brand-green placeholder:text-gray-300"
                                                        placeholder="Role Name"
                                                        type="text"
                                                        maxLength={24}
                                                        value={selectedRole.name}
                                                        autoComplete="false"
                                                        onChange={handleRoleNameChange}
                                                        {...(userHighRole <= selectedRole.priority ? { disabled: true } : {})}
                                                        disabled={userHighRole <= selectedRole.priority}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr className="border-gray-600 w-full my-2" />

                                    <h1 className="text-sm font-semibold text-white">Color</h1>

                                    <div className="flex items-center rounded mt-4 border">
                                        <button
                                            className={`flex rounded-l items-center gap-2 px-2 py-2 bg-brand-darker hover:bg-brand-default transition-all ${!selectedRole.color2 ? 'bg-brand-default' : ''}`}
                                            onClick={() => {
                                                const updatedRoles = roles.map(role =>
                                                    role.id === selectedRole.id ? { ...role, color2: null } : role
                                                );
                                                setRoles(updatedRoles);
                                                setSelectedRole({ ...selectedRole, color2: null });
                                                setIsChanged(true);
                                            }}
                                        >
                                            Solid
                                        </button>
                                        <button
                                            className={`flex items-center gap-2 px-2 py-2 bg-brand-darker hover:bg-brand-default transition-all rounded-r ${selectedRole.color2 ? 'bg-brand-default' : ''}`}
                                            onClick={() => {
                                                const updatedRoles = roles.map(role =>
                                                    role.id === selectedRole.id ? { ...role, color2: defaultGradientColors[0].color2 } : role
                                                );
                                                setRoles(updatedRoles);
                                                setSelectedRole({ ...selectedRole, color2: defaultGradientColors[0].color2 });
                                                setIsChanged(true);
                                            }}
                                        >
                                            Gradient
                                        </button>
                                    </div>
                                    {!selectedRole.color2 && (
                                        <div className="flex items-center gap-2 mt-4 mb-4">
                                            <div className="flex items-center gap-2">
                                                <div className={`flex items-start gap-2 ${userHighRole > selectedRole.priority ? '' : 'opacity-50'}`}>
                                                    <Tippy content="Default">
                                                        <div className={`h-12 w-12 rounded-full bg-white  ${userHighRole > selectedRole.priority ? "cursor-pointer" : "cursor-not-allowed"}`} onClick={() => {
                                                            if (userHighRole <= selectedRole.priority) return;
                                                            setRoleColor("#FFFFFF")
                                                        }} />
                                                    </Tippy>
                                                    <div className="relative inline-block">
                                                        <Tippy content="Custom">

                                                            <div className="w-12 h-12 rounded-full">
                                                                <div
                                                                    className={`w-12 h-12 rounded-full ${userHighRole > selectedRole.priority ? "cursor-pointer" : "cursor-not-allowed"}`}
                                                                    style={{
                                                                        background:
                                                                            selectedRole.color.toUpperCase() === "#FFFFFF"
                                                                                ? "conic-gradient(#ff0000, #ff00ff, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000, #ff00ff, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000)"
                                                                                : selectedRole.color,
                                                                    }}
                                                                    onClick={() => {
                                                                        if (userHighRole <= selectedRole.priority) return;
                                                                        const input = document.getElementById("roleColor") as HTMLInputElement;
                                                                        input.click();
                                                                    }}
                                                                />
                                                            </div>

                                                        </Tippy>
                                                        <input
                                                            type="color"
                                                            id="roleColor"
                                                            disabled={userHighRole <= selectedRole.priority}
                                                            className={`h-0 w-0 absolute`}
                                                            value={selectedRole.color}
                                                            onChange={() => {
                                                                setRoleColor(null);
                                                                selectedRole.color = selectedRole.color;
                                                            }}
                                                        />
                                                    </div>

                                                    <div className="grid grid-cols-8 gap-1">
                                                        {defaultColors.map((color) => (
                                                            <Tippy content={color.name} key={color.name + "-key"}>
                                                                <div
                                                                    className={`h-5 w-5 rounded-full ${userHighRole > selectedRole.priority ? "cursor-pointer" : "cursor-not-allowed"}`}
                                                                    style={{ backgroundColor: color.color }}
                                                                    onClick={() => {
                                                                        if (userHighRole <= selectedRole.priority) return;
                                                                        setRoleColor(color.color);
                                                                    }}
                                                                />
                                                            </Tippy>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedRole.color2 && (
                                        <div className="flex items-center gap-2 mt-4 mb-4">
                                            <div className="flex items-center gap-2">
                                                <div className={`flex items-start relative gap-2 ${userHighRole > selectedRole.priority ? '' : 'opacity-50'}`}>
                                                    <div className="flex items-start relative gap-2">
                                                        <div className="absolute top-0 left-0 w-24 h-full rounded-full" style={{ background: `linear-gradient(45deg, ${selectedRole.color}, ${selectedRole.color2})`, zIndex: -1 }} />
                                                        <Tippy content="Gradient Color 1">
                                                            <div className="relative inline-block">
                                                                <div className="w-12 h-12 rounded-full">
                                                                    <div
                                                                        className={`w-12 h-12 rounded-full  ${userHighRole > selectedRole.priority ? "cursor-pointer" : "cursor-not-allowed"}`}
                                                                        style={{ backgroundColor: selectedRole.color }}
                                                                        onClick={() => {
                                                                            if (userHighRole <= selectedRole.priority) return;
                                                                            const input = document.getElementById("roleGradientColor1") as HTMLInputElement;
                                                                            input.click();
                                                                        }}
                                                                    />
                                                                </div>
                                                                <input
                                                                    type="color"
                                                                    id="roleGradientColor1"
                                                                    disabled={userHighRole <= selectedRole.priority}
                                                                    className={`h-0 w-0 absolute`}
                                                                    value={selectedRole.color}
                                                                    onChange={(e) => {
                                                                        const updatedRoles = roles.map(role =>
                                                                            role.id === selectedRole.id ? { ...role, color: e.target.value } : role
                                                                        );
                                                                        setRoles(updatedRoles);
                                                                        setSelectedRole({ ...selectedRole, color: e.target.value });
                                                                        setIsChanged(true);
                                                                    }}
                                                                />
                                                            </div>
                                                        </Tippy>

                                                        <Tippy content="Gradient Color 2">
                                                            <div className="relative inline-block">
                                                                <div className="w-12 h-12 rounded-full">
                                                                    <div
                                                                        className={`w-12 h-12 rounded-full ${userHighRole > selectedRole.priority ? "cursor-pointer" : "cursor-not-allowed"}`}
                                                                        style={{ backgroundColor: selectedRole.color2 }}
                                                                        onClick={() => {
                                                                            if (userHighRole <= selectedRole.priority) return;
                                                                            const input = document.getElementById("roleGradientColor2") as HTMLInputElement;
                                                                            input.click();
                                                                        }}
                                                                    />
                                                                </div>
                                                                <input
                                                                    type="color"
                                                                    id="roleGradientColor2"
                                                                    disabled={userHighRole <= selectedRole.priority}
                                                                    className={`h-0 w-0 absolute`}
                                                                    value={selectedRole.color2}
                                                                    onChange={(e) => {
                                                                        const updatedRoles = roles.map(role =>
                                                                            role.id === selectedRole.id ? { ...role, color2: e.target.value } : role
                                                                        );
                                                                        setRoles(updatedRoles);
                                                                        setSelectedRole({ ...selectedRole, color2: e.target.value });
                                                                        setIsChanged(true);
                                                                    }}
                                                                />
                                                            </div>
                                                        </Tippy>
                                                    </div>


                                                    <div className="grid grid-cols-8 gap-1">
                                                        {defaultGradientColors.map((gradient) => (
                                                            <Tippy content={gradient.name} key={gradient.name + "-key"}>
                                                                <div
                                                                    className={`h-5 w-5 rounded-full ${userHighRole > selectedRole.priority ? "cursor-pointer" : "cursor-not-allowed"}`}
                                                                    style={{ background: `linear-gradient(45deg, ${gradient.color1}, ${gradient.color2})` }}
                                                                    onClick={() => {
                                                                        if (userHighRole <= selectedRole.priority) return;
                                                                        const updatedRoles = roles.map(role =>
                                                                            role.id === selectedRole.id ? { ...role, color: gradient.color1, color2: gradient.color2 } : role
                                                                        );
                                                                        setRoles(updatedRoles);
                                                                        setSelectedRole({ ...selectedRole, color: gradient.color1, color2: gradient.color2 });
                                                                        setIsChanged(true);
                                                                    }}
                                                                />
                                                            </Tippy>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <hr className="border-gray-600 w-full my-2" />

                                    <h1 className="text-sm font-semibold text-white">Role Settings</h1>

                                    <div className="mt-4 w-full">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className={`sm:flex items-center sm:justify-between w-full bg-brand-darker rounded-lg px-4 py-2 mb-2 ${userHighRole == selectedRole.priority || selectedRole.id == team.id ? 'opacity-50' : 'cursor-pointer hover:bg-brand-default'}`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <h1 className="text-sm font-semibold text-gray-500">{">"}</h1>
                                                    <div className="flex flex-col items-start w-96">
                                                        <span className="text-white text-sm font-semibold">
                                                            Display Role Separately
                                                        </span>
                                                        <span className="text-gray-400 text-xs">
                                                            If enabled, members with this role will display separately from other online members
                                                        </span>
                                                    </div>
                                                </div>
                                                <div
                                                    className="relative inline-flex items-center cursor-pointer"
                                                    onClick={() => {
                                                        if (userHighRole <= selectedRole.priority || selectedRole.id == team.id) return;
                                                        const updatedRoles = roles.map(role =>
                                                            role.id === selectedRole.id
                                                                ? {
                                                                    ...role,
                                                                    isDisplayedSeparately: !selectedRole.isDisplayedSeparately,
                                                                }
                                                                : role
                                                        );
                                                        setRoles(updatedRoles);
                                                        setSelectedRole({
                                                            ...selectedRole,
                                                            isDisplayedSeparately: !selectedRole.isDisplayedSeparately,
                                                        });
                                                        setIsChanged(true);
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only"
                                                        onChange={() => { }}
                                                        checked={selectedRole.isDisplayedSeparately}
                                                    />
                                                    <div
                                                        className={`
                                                    block w-14 h-8 rounded-full
                                                    ${selectedRole.isDisplayedSeparately ? 'bg-brand-green' : 'bg-brand-profile'}
                                                    ${userHighRole <= selectedRole.priority || selectedRole.id == teamId ? 'cursor-not-allowed' : ''}
                                                    `}
                                                    >
                                                        <div
                                                            className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${selectedRole.isDisplayedSeparately ? 'translate-x-6' : 'translate-x-0'
                                                                }`}
                                                        />
                                                    </div>
                                                </div>

                                            </div>

                                        </div>
                                    </div>

                                    <hr className="border-gray-600 w-full my-2" />

                                    <h1 className="text-sm font-semibold text-white">Permissions</h1>
                                    <div className="mt-4 w-full">

                                        {permissionsDetails.map((permission) => (
                                            <div
                                                key={permission.name}
                                                className={`sm:flex items-center sm:justify-between w-full bg-brand-darker rounded-lg px-4 py-2 mb-2 ${Boolean(BigInt(me ?? '0') & permission.map!) && userHighRole > selectedRole.priority
                                                    ? 'cursor-pointer hover:bg-brand-default' : 'opacity-50'}`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <h1 className="text-2xl font-semibold text-gray-500">{">"}</h1>
                                                    <div className="flex flex-col items-start w-96">
                                                        <span className="text-white font-semibold text-sm">{permission.name}</span>
                                                        <span className="text-gray-400 text-xs">{permission.description}</span>
                                                    </div>
                                                </div>
                                                <div
                                                    className="relative inline-flex items-center cursor-pointer"
                                                    onClick={() => {
                                                        if (!(BigInt(me ?? '0') & permission.map!) || userHighRole <= selectedRole.priority) return;
                                                        const updatedRoles = roles.map(role =>
                                                            role.id === selectedRole.id
                                                                ? {
                                                                    ...role,
                                                                    permissions:
                                                                        role.permissions ^
                                                                        (Number(permission.map ?? 0) ?? 0),
                                                                }
                                                                : role
                                                        );
                                                        setRoles(updatedRoles);
                                                        setSelectedRole({
                                                            ...selectedRole,
                                                            permissions:
                                                                selectedRole.permissions ^
                                                                (Number(permission.map ?? 0) ?? 0),
                                                        });
                                                        setIsChanged(true);
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="sr-only"
                                                        onChange={() => { }}
                                                        checked={permissions.get(permission.onMap) !== undefined && (selectedRole.permissions & Number(permissions.get(permission.onMap) ?? 0)) !== 0}
                                                    />
                                                    <div
                                                        className={`block w-14 h-8 rounded-full ${selectedRole.permissions &
                                                            Number(permission.map ?? 0)
                                                            ? 'bg-brand-green'
                                                            : 'bg-brand-profile'
                                                            }
                                                    ${!(BigInt(me ?? '0') & permission.map!) || userHighRole <= selectedRole.priority ? 'cursor-not-allowed' : ''}`}
                                                    >
                                                        <div
                                                            className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${selectedRole.permissions & (Number(permission.map ?? 0)) ? 'translate-x-6' : 'translate-x-0'}`}
                                                        />
                                                    </div>
                                                </div>

                                            </div>
                                        ))}
                                    </div>
                                </ErrorBoundary>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
